import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  Attachment,
  AttachmentsService,
} from '@kinet-open-network/client/attachments/data-access';
import { ErrorHandlerContext } from '@kinet-open-network/client/core/api-client';
import { confirmDialog, toast } from '@kinet-open-network/client/shared/ui';
import { AbilitySubject } from '@kinet-open-network/shared/data-access/model';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBar } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import {
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  forkJoin,
  map,
  of,
  startWith,
  switchMap,
  throwError,
} from 'rxjs';

let ATTACHMENT_ID_COUNTER = 0;

export interface IGalleryLinkedTo {
  kind?: AbilitySubject;
  id?: string | number;
  field?: string;
}

interface GalleryProps extends FormlyFieldProps {
  accept?: string;
  linkedTo?: IGalleryLinkedTo;
  multiple?: boolean;
  size?: 'default' | 'small';
}

export interface IAttachmentItem {
  id: string;
  name: string;
  mimeType: string;
  dataUrl: string;
  cancel?: () => void;
  uploadProgress?: number;
  corrupted?: boolean;
  corruptedDetail?: string;
}

interface UploadObserver {
  onUpload: (attachment: Attachment) => void;
  onError: (error: unknown) => void;
  onProgress: (progress: number) => void;
  onCancel: () => void;
}

export interface FormlyGalleryFieldConfig
  extends FormlyFieldConfig<GalleryProps> {}

@Component({
  selector: 'kon-gallery-type',
  imports: [
    FormlyModule,
    SelectModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    ProgressBar,
    Dialog,
  ],
  template: `
    <!-- <input [formControl]="formControl" /> -->
    <div class="flex items-center justify-start flex-wrap">
      @for (item of items(); track item.id) {
        <div
          class="relative rounded overflow-hidden mb-2 mr-2 group h-24 w-24 group-[.size-sm]:h-16 group-[.size-sm]:w-16"
        >
          @if (isImage(item)) {
            <img
              [src]="item.dataUrl"
              [alt]="item.name"
              [title]="item.name"
              class="object-cover h-full w-full"
            />
          } @else {
            @if (item.corrupted) {
              <div
                [pTooltip]="item.corruptedDetail"
                [showDelay]="200"
                tooltipPosition="top"
                class="object-cover bg-gray-200 text-gray-500 border-gray-500 w-full h-full flex flex-col justify-center items-center gap-1"
              >
                <i class="pi pi-exclamation-triangle"></i>
                <span class="text-xs text-center">Príloha poškodená</span>
              </div>
            } @else {
              <div
                class="object-cover bg-white text-black border-black w-full h-full flex flex-col justify-center items-center"
              >
                <i class="pi pi-paperclip"></i>
                @if (item.name) {
                  <span>{{ item.name }}</span>
                }
              </div>
            }
          }

          @if (item.uploadProgress !== undefined) {
            <div
              class="absolute inset-x-0 top-0 flex justify-center items-center bg-white/80"
            >
              <p-progress-bar
                class="w-full"
                [mode]="
                  item.uploadProgress > 0 ? 'determinate' : 'indeterminate'
                "
                [value]="item.uploadProgress"
                [showValue]="false"
                [style]="{ height: '5px' }"
              />
            </div>

            @if (item.cancel) {
              <div
                class="absolute top-0 w-full h-full group-hover:bg-black cursor-pointer grid items-center group group-hover:bg-opacity-50"
                (click)="item.cancel()"
              >
                <i
                  class="pi pi-times text-white text-2xl justify-self-center opacity-0 group-hover:opacity-100 transition-opacity"
                ></i>
              </div>
            }
          } @else {
            @if (!item.corrupted) {
              <div
                class="absolute top-0 w-full h-full group-hover:bg-black cursor-pointer grid items-center group group-hover:bg-opacity-50"
                (click)="downloadItem(item)"
              >
                <i
                  class="pi pi-download text-white text-2xl justify-self-center opacity-0 group-hover:opacity-100 transition-opacity"
                ></i>
              </div>
            }

            @if (isPreviewable(item)) {
              <div class="absolute left-0 top-0 p-0.5">
                <button
                  pButton
                  type="button"
                  pTooltip="Zväčšiť"
                  icon="pi pi-search-plus"
                  class="p-button-rounded p-button-text p-button-sm p-button-primary"
                  (click)="openPreview(item)"
                ></button>
              </div>
            }

            @if (!formControl.disabled) {
              <div class="absolute right-0 top-0 p-0.5">
                <button
                  pButton
                  type="button"
                  pTooltip="Odstrániť"
                  icon="pi pi-times"
                  class="p-button-rounded p-button-text p-button-sm p-button-danger"
                  (click)="removeItem(item)"
                ></button>
              </div>
            }
          }
        </div>
      } @empty {
        @if (formControl.disabled) {
          <div
            class="flex justify-center items-center bg-gray-50 border-2 border-dashed border-gray-500 rounded-2xl h-24 w-24 group-[.size-sm]:h-16 group-[.size-sm]:w-16"
          >
            <span class="text-gray-500 text-center">Žiadne obrázky</span>
          </div>
        }
      }

      @if (!formControl.disabled) {
        <div
          class="flex justify-center items-center mb-2 bg-primary-50 border-2 border-dashed border-primary-500 cursor-pointer rounded-2xl hover:bg-primary-100 transition-colors duration-100 h-24 w-24 group-[.size-sm]:h-16 group-[.size-sm]:w-16"
          (click)="addImages()"
        >
          <i class="pi pi-plus text-primary-500"></i>
        </div>
      }
    </div>

    <input
      type="file"
      [multiple]="props.multiple !== false"
      (change)="onFileChange($event)"
      class="hidden invisible"
      [accept]="props.accept"
      #fileInput
    />

    <p-dialog
      [visible]="previewDialogVisible()"
      (visibleChange)="onPreviewDialogVisibleChange($event)"
      [modal]="true"
      [appendTo]="'body'"
      styleClass="md:max-w-[80vw]"
      [header]="previewItem()?.name ?? ''"
    >
      <img [src]="previewImageSrc()" class="w-full h-auto object-contain" />
    </p-dialog>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'group',
    '[class.size-sm]': "props.size === 'small'",
  },
})
export class FormlyGalleryTypeComponent
  extends FieldType<FieldTypeConfig<GalleryProps>>
  implements OnInit
{
  private attachmentsService = inject(AttachmentsService);
  private destroyRef = inject(DestroyRef);
  private fileInput = viewChild<ElementRef>('fileInput');
  private attachmentsCache = new Map<string, Attachment | null>();

  override defaultOptions: Partial<FieldTypeConfig<GalleryProps>> = {
    props: {
      accept: 'image/*',
    },
  };

  items = signal<IAttachmentItem[]>([]);

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.formControl.value),
        map<unknown, string[]>((value) => {
          if (!Array.isArray(value)) {
            if (this.props.multiple === false && typeof value === 'string') {
              return [value];
            }

            return [];
          }

          return value;
        }),
        distinctUntilChanged((previous, current) => {
          return JSON.stringify(previous) === JSON.stringify(current);
        }),
        switchMap((ids) => {
          if (ids.length === 0) {
            return of([]);
          }

          const idsToFetch = ids.filter((id) => !this.attachmentsCache.has(id));
          const cachedAttachments = ids
            .filter((id) => this.attachmentsCache.has(id))
            .map((id) => this.attachmentsCache.get(id) ?? { id });

          if (idsToFetch.length === 0) {
            return of(cachedAttachments);
          }

          const httpCtx = new HttpContext();

          httpCtx.set(ErrorHandlerContext, {
            ignoreBelowStatus: 500,
          });

          return forkJoin(
            idsToFetch.map((id) =>
              this.attachmentsService.getById(id, { context: httpCtx }).pipe(
                catchError((err) => {
                  if (err instanceof HttpErrorResponse && err.status === 404) {
                    return of(null);
                  }

                  return throwError(() => err);
                }),
              ),
            ),
          ).pipe(
            map((fetchedAttachments) => {
              idsToFetch.forEach((id) => {
                this.attachmentsCache.set(
                  id,
                  fetchedAttachments.find((att) => att?.id === id) || null,
                );
              });

              return ids.map((id) => this.attachmentsCache.get(id) ?? { id });
            }),
          );
        }),
      )
      .subscribe((attachments) => {
        this.items.set(
          attachments.map((attachment) => {
            if (attachment instanceof Attachment) {
              return {
                id: attachment.id,
                name: attachment.name ?? 'Untitled',
                mimeType: attachment.mimeType ?? 'application/octet-stream',
                dataUrl: attachment.dataUrl ?? '',
              };
            }

            return {
              id: attachment.id,
              name: 'Untitled',
              mimeType: 'application/octet-stream',
              dataUrl: '',
              corrupted: true,
              corruptedDetail: 'Príloha neexistuje',
            };
          }),
        );
      });
  }

  isImage(attachment: IAttachmentItem): boolean {
    return attachment.mimeType.includes('image');
  }

  isPreviewable(attachment: IAttachmentItem): boolean {
    return this.isImage(attachment);
  }

  previewItem = signal<IAttachmentItem | null>(null);

  previewDialogVisible = computed(() => this.previewItem() !== null);

  previewImageSrc = computed(() => {
    return this.previewItem()?.dataUrl || '';
  });

  openPreview(attachment: IAttachmentItem) {
    this.previewItem.set(attachment);
  }

  closePreview() {
    this.previewItem.set(null);
  }

  onPreviewDialogVisibleChange(visible: boolean) {
    if (!visible) {
      this.closePreview();
    }
  }

  async onFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    await Promise.all(
      Array.from(files).map(async (file) => {
        const dataUrl = await this.readFile(file);
        const itemId = `temp-${++ATTACHMENT_ID_COUNTER}`;

        const cancelFn = this.uploadFile(
          { file, dataUrl },
          {
            onProgress: (progress) => {
              this.items.update((items) => [
                ...items.map((item) => {
                  if (item.id === itemId) {
                    return {
                      ...item,
                      uploadProgress: progress,
                    };
                  }

                  return item;
                }),
              ]);
            },
            onCancel: () => {
              this.items.update((items) =>
                items.filter((item) => item.id !== itemId),
              );
            },
            onUpload: (attachment) => {
              this.items.update((items) => [
                ...items.map((item) => {
                  if (item.id === itemId) {
                    return {
                      id: attachment.id,
                      name: attachment.name ?? item.name,
                      mimeType: attachment.mimeType ?? item.mimeType,
                      dataUrl: item.dataUrl,
                      progress: undefined,
                      cancel: undefined,
                    };
                  }

                  return item;
                }),
              ]);

              if (this.props.multiple === false) {
                this.formControl.setValue(attachment.id);
                this.formControl.markAsDirty();
                return;
              } else {
                const value = this.formControl.value || [];

                this.formControl.setValue([
                  ...(Array.isArray(value) ? value : []),
                  attachment.id,
                ]);
                this.formControl.markAsDirty();
              }
            },
            onError: (_error) => {
              toast({
                severity: 'danger',
                summary: 'Chyba',
                detail: 'Nahrávanie prílohy zlyhalo.',
              });
            },
          },
        );

        const newItem: IAttachmentItem = {
          id: itemId,
          name: file.name,
          mimeType: file.type,
          dataUrl,
          uploadProgress: 0,
          cancel: cancelFn,
        };

        this.items.update((items) => [...items, newItem]);
      }),
    );
    target.value = '';
  }

  async removeItem(attachment: IAttachmentItem) {
    confirmDialog({
      header: 'Odstrániť prílohu',
      message: 'Naozaj chcete odstrániť túto prílohu?',
      acceptLabel: 'Odstrániť',
      rejectLabel: 'Zrušiť',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        try {
          await firstValueFrom(
            this.attachmentsService.delete(
              attachment.id,
              attachment.corrupted === true,
            ),
          );

          if (this.props.multiple === false) {
            this.formControl.setValue(null);
          } else {
            const value = this.formControl.value || [];

            if (Array.isArray(value)) {
              this.formControl.setValue(
                value.filter((_id: string) => _id !== attachment.id),
              );
            } else {
              this.formControl.setValue([]);
            }
          }

          this.formControl.markAsDirty();
        } catch (error) {
          toast({
            severity: 'danger',
            summary: 'Chyba',
            detail: 'Nemožno odstrániť prílohu.',
          });

          throw error;
        }
      },
    });
  }

  async addImages() {
    this.fileInput()?.nativeElement.click();
  }

  private readFile(file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (e) => {
        reject(e);
      };

      reader.readAsDataURL(file);
    });
  }

  private uploadFile(
    data: {
      file: File;
      dataUrl: string;
    },
    observer: Partial<UploadObserver>,
  ): () => void {
    const payload = this.shouldUseBase64Upload() ? data.dataUrl : data.file;

    const subscription = this.attachmentsService
      .create(
        {
          name: data.file.name,
          extra: {
            linkedEntityType: this.props.linkedTo?.kind,
            linkedEntityId: this.props.linkedTo?.id,
            linkedField: String(this.props.linkedTo?.field || this.key),
          },
        },
        payload,
        observer.onProgress,
      )
      .subscribe({
        next: (attachment) => {
          observer.onUpload?.(attachment);
        },
        error: (error) => {
          observer.onError?.(error);
        },
        complete: () => {
          // no-op
        },
      });

    const cancelFn = () => {
      subscription.unsubscribe();
      observer.onCancel?.();
    };

    return cancelFn;
  }

  private shouldUseBase64Upload(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const isAppleMobileUa = /iPad|iPhone|iPod/i.test(ua);

    // iPadOS can report Mac platform while still using the iOS network stack.
    const isIpadOsDesktopUa =
      platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    return isAppleMobileUa || isIpadOsDesktopUa;
  }

  async downloadItem(attachment: IAttachmentItem) {
    return await this.attachmentsService.download(
      attachment.id,
      attachment.name,
    );
  }
}
